import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export type CommitmentMode = 'counted' | 'always_on';

export interface BrandCommitment {
  id: number;
  brand: string;
  year: number;
  slot: string;
  custom_label: string;
  mode: CommitmentMode;
  target: number;
  /** Used when the slot is counted-but-manual (Collection page, Custom). */
  delivered_manual: number;
  /** Used when the slot is always-on. */
  delivered_state: boolean;
  /** Server-computed delivery count (the value the UI reads). */
  delivered: number;
  notes: string;
}

export interface CreateCommitmentInput {
  year: number;
  slot: string;
  custom_label?: string;
  mode: CommitmentMode;
  target?: number;
  delivered_manual?: number;
  delivered_state?: boolean;
  notes?: string;
}

const listKey = (brandId: string, year: number) =>
  ['brand-commitments', brandId, year] as const;

export const useCommitments = (brandId: string | undefined, year: number) =>
  useQuery<BrandCommitment[]>({
    queryKey: listKey(brandId ?? '', year),
    queryFn: async () => {
      const res = await axios.get(
        `api/brands/${brandId}/commitments/?year=${year}`,
      );
      return res.data;
    },
    enabled: !!brandId,
  });

export const useCreateCommitment = (brandId: string, year: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCommitmentInput): Promise<BrandCommitment> => {
      const res = await axios.post(
        `api/brands/${brandId}/commitments/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId, year) });
    },
  });
};

export const usePatchCommitment = (brandId: string, year: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<BrandCommitment>;
    }): Promise<BrandCommitment> => {
      const res = await axios.patch(
        `api/brands/${brandId}/commitments/${id}/`,
        patch,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId, year) });
    },
  });
};

export const useDeleteCommitment = (brandId: string, year: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`api/brands/${brandId}/commitments/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId, year) });
    },
  });
};
